import React from 'react';
import { Text } from 'wix-ui-tpa/cssVars';
import { FavoriteFillSmall } from '@wix/wix-ui-icons-common/on-stage';

import { MenuItem } from '../../../../types/menu';

import { classes, st } from './DishItem.st.css';
import { dishItemDataHooks } from './constants';
import { useHooks } from './useHooks';

type DishItemProps = {
  item: MenuItem;
  menuId: string;
  sectionId: string;
  onClick: (item: MenuItem, menuId: string, sectionId: string) => void;
  cartQuantity?: number;
  className?: string;
};

const DishItem: React.FC<DishItemProps> = ({
  item,
  menuId,
  sectionId,
  onClick,
  cartQuantity = 0,
  className,
}) => {
  const {
    layoutSize,
    inStock,
    isCardLayout,
    isImageLeft,
    shownLabels,
    extraLabelCount,
    shouldShowImage,
    shouldShowDescription,
    shouldShowLabels,
    shouldShowFeatured,
    shouldShowLabelsRow,
    handleClick,
    handleKeyDown,
    priceText,
    imageUrl,
    imageAlt,
    featuredLabel,
  } = useHooks({ item, menuId, sectionId, onClick });

  return (
    <div
      className={st(
        classes.root,
        {
          [layoutSize]: true,
          unavailable: !inStock,
          card: isCardLayout,
          imageLeft: isImageLeft,
        },
        className,
      )}
      data-hook={dishItemDataHooks.root()}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-haspopup="true"
    >
      <div className={classes.contentRow}>
        <div className={classes.textContent}>
          <div className={classes.topContent}>
            <div className={classes.titleRow}>
              {cartQuantity > 0 && (
                <span
                  className={classes.cartBadge}
                  data-hook={dishItemDataHooks.cartBadge()}
                >
                  {cartQuantity}
                </span>
              )}
              <Text
                className={classes.title}
                data-hook={dishItemDataHooks.title()}
                tagName="p"
              >
                {item.name}
              </Text>
            </div>
            <div className={classes.descriptionBlock}>
              {shouldShowDescription && (
                <Text
                  className={classes.description}
                  data-hook={dishItemDataHooks.description()}
                  tagName="p"
                >
                  {item.description}
                </Text>
              )}
              {shouldShowLabelsRow && (
                <div
                  className={classes.labelsRow}
                  data-hook={dishItemDataHooks.labels()}
                >
                  {shouldShowFeatured && (
                    <span
                      className={classes.featuredBadge}
                      data-hook={dishItemDataHooks.featuredBadge()}
                      aria-label={featuredLabel}
                    >
                      <FavoriteFillSmall />
                    </span>
                  )}
                  {shouldShowLabels && (
                    <>
                      {shownLabels.map((label) => (
                        <img
                          key={label.id}
                          className={classes.labelIcon}
                          src={label.icon!.url ?? label.icon!.id}
                          alt={label.name}
                        />
                      ))}
                      {extraLabelCount > 0 && (
                        <Text
                          className={classes.labelCounter}
                          data-hook={dishItemDataHooks.labelCounter()}
                          tagName="span"
                        >
                          +{extraLabelCount}
                        </Text>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <Text
            className={classes.price}
            data-hook={dishItemDataHooks.price()}
            tagName="p"
          >
            {priceText}
          </Text>
        </div>
        {shouldShowImage && (
          <div className={classes.imageWrapper}>
            <img
              className={classes.image}
              src={imageUrl}
              alt={imageAlt}
              data-hook={dishItemDataHooks.image()}
              loading="lazy"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DishItem;
